import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Plus, 
  Edit, 
  Trash2, 
  Phone,
  Mail,
  Wrench,
  Save,
  X
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import api from "../lib/api";

const ServiceProviderManagement = () => {
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    category: 'Plumber',
    experience: '',
    location: 'Greater Noida',
    specialization: ''
  });

  useEffect(() => {    fetchProviders();
  }, [navigate]);

  const fetchProviders = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/service-providers`);
      setProviders(response.data.items || []);
    } catch (error) {
      console.error('Error fetching providers:', error);
      toast.error('Failed to load service providers');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingProvider) {
        await api.patch(`/service-providers/${editingProvider.id}`, formData);
        toast.success('Service provider updated successfully!');
      } else {
        await api.post(`/service-providers`, formData);
        toast.success('Service provider added successfully!');
      }

      setShowForm(false);
      setEditingProvider(null);
      resetForm();
      fetchProviders();
    } catch (error) {
      console.error('Error saving provider:', error);
      toast.error(error.response?.data?.detail || 'Failed to save service provider');
    }
  };

  const handleEdit = (provider) => {
    setEditingProvider(provider);
    setFormData({
      name: provider.name,
      phone: provider.phone,
      email: provider.email || '',
      category: provider.category,
      experience: provider.experience,
      location: provider.location,
      specialization: provider.specialization || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (providerId, providerName) => {
    if (!window.confirm(`Are you sure you want to delete ${providerName}?`)) {
      return;
    }

    try {
      await api.delete(`/service-providers/${providerId}`);
      toast.success('Service provider deleted successfully!');
      fetchProviders();
    } catch (error) {
      console.error('Error deleting provider:', error);
      toast.error('Failed to delete service provider');
    }
  };

  const toggleActive = async (providerId, currentStatus) => {
    try {
      await api.patch(`/service-providers/${providerId}`, { active: !currentStatus });
      toast.success(`Provider ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
      fetchProviders();
    } catch (error) {
      console.error('Error updating provider status:', error);
      toast.error('Failed to update provider status');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      category: 'Plumber',
      experience: '',
      location: 'Greater Noida',
      specialization: ''
    });
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingProvider(null);
    resetForm();
  };

  const getCategoryIcon = (category) => {
    const icons = {
      Plumber: '🔧',
      Electrician: '⚡',
      Painter: '🎨',
      Carpenter: '🔨'
    };
    return icons[category] || '🛠️';
  };

  const getCategoryColor = (category) => {
    const colors = {
      Plumber: 'bg-blue-100 text-blue-800',
      Electrician: 'bg-yellow-100 text-yellow-800',
      Painter: 'bg-purple-100 text-purple-800',
      Carpenter: 'bg-orange-100 text-orange-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading service providers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/admin/dashboard')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Service Provider Management</h1>
                <p className="text-sm text-gray-600">Manage Plumbers, Electricians, Painters & Carpenters</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {['Plumber', 'Electrician', 'Painter', 'Carpenter'].map((category) => {
            const count = providers.filter(p => p.category === category).length;
            return (
              <Card key={category}>
                <CardContent className="p-4 text-center">
                  <div className="text-3xl mb-2">{getCategoryIcon(category)}</div>
                  <p className="text-2xl font-bold text-gray-900">{count}</p>
                  <p className="text-sm text-gray-600">{category}s</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Add Button */}
        {!showForm && (
          <div className="mb-6">
            <Button onClick={() => setShowForm(true)} size="lg" className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-5 w-5 mr-2" />
              Add Service Provider
            </Button>
          </div>
        )}

        {/* Form */}
        {showForm && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>{editingProvider ? 'Edit Service Provider' : 'Add New Service Provider'}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Name *</label>
                    <Input name="name" value={formData.name} onChange={handleInputChange} placeholder="Provider name" required />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Phone Number *</label>
                    <Input name="phone" value={formData.phone} onChange={handleInputChange} placeholder="9876543210" required />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Email</label>
                    <Input name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="email@example.com" />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Category *</label>
                    <Select name="category" value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Plumber">🔧 Plumber</SelectItem>
                        <SelectItem value="Electrician">⚡ Electrician</SelectItem>
                        <SelectItem value="Painter">🎨 Painter</SelectItem>
                        <SelectItem value="Carpenter">🔨 Carpenter</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Experience *</label>
                    <Input name="experience" value={formData.experience} onChange={handleInputChange} placeholder="5 years" required />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Location *</label>
                    <Input name="location" value={formData.location} onChange={handleInputChange} placeholder="Greater Noida" required />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Specialization</label>
                  <Textarea name="specialization" value={formData.specialization} onChange={handleInputChange} placeholder="e.g., Pipeline installation, Bathroom fittings" rows={3} />
                </div>

                <div className="flex gap-4">
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    <Save className="h-4 w-4 mr-2" />
                    {editingProvider ? 'Update Provider' : 'Add Provider'}
                  </Button>
                  <Button type="button" variant="outline" onClick={cancelForm}>
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Providers List */}
        <Card>
          <CardHeader>
            <CardTitle>All Service Providers ({providers.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {providers.length === 0 ? (
              <div className="text-center py-12">
                <Wrench className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No service providers added yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {providers.map((provider) => (
                  <Card key={provider.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <span className="text-3xl">{getCategoryIcon(provider.category)}</span>
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">{provider.name}</h3>
                              <Badge className={getCategoryColor(provider.category)}>{provider.category}</Badge>
                            </div>
                            {!provider.active && (
                              <Badge className="bg-gray-200 text-gray-600">Inactive</Badge>
                            )}
                          </div>
                          <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-600">
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-blue-600" />
                              <a href={`tel:${provider.phone}`} className="hover:text-blue-600">{provider.phone}</a>
                            </div>
                            {provider.email && (
                              <div className="flex items-center gap-2">
                                <Mail className="h-4 w-4 text-blue-600" />
                                <span>{provider.email}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <Wrench className="h-4 w-4 text-blue-600" />
                              <span>{provider.experience} experience</span>
                            </div>
                          </div>
                          {provider.specialization && (
                            <p className="mt-2 text-sm text-gray-600"><strong>Specialization:</strong> {provider.specialization}</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => toggleActive(provider.id, provider.active)}>
                            {provider.active ? 'Deactivate' : 'Activate'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleEdit(provider)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(provider.id, provider.name)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ServiceProviderManagement;
